import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import {
	DataSecurityWithAllFeaturesAndRanksTct,
	DayCountConvention,
	PeriodUnitType,
	SecurityInterestDefinitionPrincipal,
} from '@dealstack/shared/lib/generated';
import { useTranslationCapManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.tsx';
import {
	SecurityInterestDefinitionFormDto,
	SecurityInterestDefinitionShape,
} from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Securities/SecurityDetails/SecurityFeatures/IncomeRights/incomeRights.zod.ts';

export enum INTEREST_DEFINITION_FORM_FIELD {
	accrualPeriod = 'accrualPeriod',
	interestBaseRate = 'interestBaseRate',
	interestFloatingRate = 'interestFloatingRate',
	seniorityIncome = 'seniorityIncome',
	deemedStartDate = 'deemedStartDate',
	seniorityRankInterest = 'seniorityRankInterest',
	seniorityRankPrincipal = 'seniorityRankPrincipal',
	compoundingPeriod = 'compoundingPeriod',
	firstInterestDate = 'firstInterestDate',
	dayCountConvention = 'dayCountConvention',
	principal = 'principal',
	principalValue = 'principalValue',
}

export const useIncomeRightsController = (
	security: DataSecurityWithAllFeaturesAndRanksTct | null
) => {
	const tCapManage = useTranslationCapManage();

	const interestDefinitionDefaultValues = useMemo(
		() =>
			({
				id: security?.securityInterestDefinition?.id,
				accrualPeriod:
					security?.securityInterestDefinition?.accrualPeriod || PeriodUnitType.DAILY,
				interestBaseRate: security?.securityInterestDefinition?.interestBaseRate,
				interestFloatingRate: security?.securityInterestDefinition?.interestFloatingRate,
				deemedStartDate: security?.securityInterestDefinition?.deemedStartDate,
				compoundingPeriod: security?.securityInterestDefinition?.compoundingPeriod,
				firstInterestDate: security?.securityInterestDefinition?.firstInterestDate,
				dayCountConvention:
					security?.securityInterestDefinition?.dayCountConvention ??
					DayCountConvention.ACTUAL_ACTUAL_ISDA,
				principal:
					security?.securityInterestDefinition?.principal ??
					SecurityInterestDefinitionPrincipal.ISSUE_COST_PER_UNIT,
				principalValue: security?.securityInterestDefinition?.principalValue,
			}) as SecurityInterestDefinitionFormDto,
		[security]
	);

	const form = useForm<SecurityInterestDefinitionFormDto>({
		mode: 'all',
		resolver: zodResolver(SecurityInterestDefinitionShape(tCapManage)),
		defaultValues: interestDefinitionDefaultValues,
		shouldFocusError: false,
	});

	const principal = form.watch(INTEREST_DEFINITION_FORM_FIELD.principal);

	useEffect(() => {
		if (principal === SecurityInterestDefinitionPrincipal.ISSUE_COST_PER_UNIT) {
			void form.trigger(INTEREST_DEFINITION_FORM_FIELD.principalValue);
		}
	}, [principal]);

	const resetForm = () => {
		form.reset(interestDefinitionDefaultValues);
	};

	return {
		form,
		resetForm,
	};
};
